import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

import { OrgProvider } from "@/contexts/OrgContext";
import { LanguageProvider } from "@/contexts/LanguageContext";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SupplierProfileGuard } from "@/components/SupplierProfileGuard";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import LandingPage from "./pages/LandingPage";
const AuthPage = lazy(() => import("./pages/AuthPage"));
const OAuthConsentPage = lazy(() => import("./pages/OAuthConsentPage"));

// Lazy-loaded pages for code splitting
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AdminHome = lazy(() => import("./pages/admin/AdminHome"));
const AdminDirectory = lazy(() => import("./pages/admin/AdminDirectory"));
const AdminSegments = lazy(() => import("./pages/admin/AdminSegments"));
const AdminToolBuilder = lazy(() => import("./pages/admin/AdminToolBuilder"));
const AdminTools = lazy(() => import("./pages/admin/AdminTools"));
const AdminDeployTool = lazy(() => import("./pages/admin/AdminDeployTool"));
const AdminResults = lazy(() => import("./pages/admin/AdminResults"));
const AdminResultDetail = lazy(() => import("./pages/admin/AdminResultDetail"));
const AdminReporting = lazy(() => import("./pages/admin/AdminReporting"));
const AdminEcosystemTracking = lazy(() => import("./pages/admin/AdminEcosystemTracking"));
const AdminEvents = lazy(() => import("./pages/admin/AdminEvents"));
const AdminResources = lazy(() => import("./pages/admin/AdminResources"));
const AdminSupplierDetail = lazy(() => import("./pages/admin/AdminSupplierDetail"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminCommunity = lazy(() => import("./pages/admin/AdminCommunity"));
const AdminApplications = lazy(() => import("./pages/admin/AdminApplications"));
const AdminPlans = lazy(() => import("./pages/admin/AdminPlans"));
const AdminPlanBuilder = lazy(() => import("./pages/admin/AdminPlanBuilder"));
const SupplierDashboard = lazy(() => import("./pages/supplier/SupplierDashboard"));
const SupplierProfile = lazy(() => import("./pages/supplier/SupplierProfile"));
const SupplierAssignedTools = lazy(() => import("./pages/supplier/SupplierAssignedTools"));
const SupplierPerformance = lazy(() => import("./pages/supplier/SupplierPerformance"));
const SupplierCommunity = lazy(() => import("./pages/supplier/SupplierCommunity"));
const SupplierEvents = lazy(() => import("./pages/supplier/SupplierEvents"));
const SupplierResources = lazy(() => import("./pages/supplier/SupplierResources"));
const SupplierPlans = lazy(() => import("./pages/supplier/SupplierPlans"));
const SupplierHelp = lazy(() => import("./pages/supplier/SupplierHelp"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const AuthRedirect = () => {
  const { user, role, loading } = useAuth();
  if (loading) return null;

  // If arriving via invite link, redirect to set password
  const hash = window.location.hash;
  if (hash.includes("type=invite") || hash.includes("type=signup")) {
    return <Navigate to={`/reset-password${hash}`} replace />;
  }

  // Preserve OAuth consent (or other same-origin) return targets after sign-in.
  const nextParam = new URLSearchParams(window.location.search).get("next");
  const safeNext = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : null;

  if (!user) return <AuthPage />;
  // User is signed in but role hasn't resolved yet — avoid a redirect loop
  // with ProtectedRoute by staying put until role is known.
  if (!role) {
    if (safeNext) return <Navigate to={safeNext} replace />;
    return <AuthPage />;
  }
  if (safeNext) return <Navigate to={safeNext} replace />;
  return <Navigate to={role === "admin" ? "/admin" : "/supplier"} replace />;
};

const AppRoutes = () => {

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth" element={<AuthRedirect />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/.lovable/oauth/consent" element={<OAuthConsentPage />} />

        {/* Admin routes */}
        <Route element={<ProtectedRoute requiredRole="admin"><DashboardLayout role="admin" /></ProtectedRoute>}>
          <Route path="/admin" element={<AdminHome />} />
          <Route path="/admin/directory" element={<AdminDirectory />} />
          <Route path="/admin/segments" element={<AdminSegments />} />
          <Route path="/admin/applications" element={<AdminApplications />} />
          <Route path="/admin/tools" element={<AdminTools />} />
          <Route path="/admin/tools/new" element={<AdminToolBuilder />} />
          <Route path="/admin/tools/:toolId/edit" element={<AdminToolBuilder />} />
          <Route path="/admin/deploy" element={<AdminDeployTool />} />
          <Route path="/admin/results" element={<AdminResults />} />
          <Route path="/admin/results/:toolId" element={<AdminResultDetail />} />
          <Route path="/admin/reporting" element={<AdminReporting />} />
          <Route path="/admin/tracking" element={<AdminEcosystemTracking />} />
          <Route path="/admin/events" element={<AdminEvents />} />
          <Route path="/admin/resources" element={<AdminResources />} />
          <Route path="/admin/plans" element={<AdminPlans />} />
          <Route path="/admin/plans/new" element={<AdminPlanBuilder />} />
          <Route path="/admin/plans/:planId/edit" element={<AdminPlanBuilder />} />
          <Route path="/admin/suppliers" element={<Navigate to="/admin/directory" replace />} />
          <Route path="/admin/suppliers/:profileId" element={<AdminSupplierDetail />} />
          <Route path="/admin/community" element={<AdminCommunity />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/help" element={<SupplierHelp />} />
        </Route>

        {/* Supplier routes */}
        <Route element={<ProtectedRoute requiredRole="supplier"><DashboardLayout role="supplier" /></ProtectedRoute>}>
          <Route path="/supplier" element={<SupplierProfileGuard><SupplierDashboard /></SupplierProfileGuard>} />
          <Route path="/supplier/profile" element={<SupplierProfileGuard><SupplierProfile /></SupplierProfileGuard>} />
          <Route path="/supplier/tools" element={<SupplierProfileGuard><SupplierAssignedTools /></SupplierProfileGuard>} />
          <Route path="/supplier/performance" element={<SupplierProfileGuard><SupplierPerformance /></SupplierProfileGuard>} />
          <Route path="/supplier/community" element={<SupplierProfileGuard><SupplierCommunity /></SupplierProfileGuard>} />
          <Route path="/supplier/events" element={<SupplierProfileGuard><SupplierEvents /></SupplierProfileGuard>} />
          <Route path="/supplier/resources" element={<SupplierProfileGuard><SupplierResources /></SupplierProfileGuard>} />
          <Route path="/supplier/plans" element={<SupplierProfileGuard><SupplierPlans /></SupplierProfileGuard>} />
          <Route path="/supplier/help" element={<SupplierProfileGuard><SupplierHelp /></SupplierProfileGuard>} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <OrgProvider>
                <AppRoutes />
            </OrgProvider>
          </LanguageProvider>
        </AuthProvider>

      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
